import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  actionHref,
  actionsFor,
  type SheetsHealth,
  type SheetsHealthAction,
} from "../lib/sheets-health";
import { mapSheetsColumns } from "../lib/sheets-health-api";
import { currentReturnPath, startSheetsSignIn } from "../lib/sheets-oauth";
import { useAuth } from "../lib/useAuth";
import { Button } from "./ui/Button";
import { SheetsColumnMapDialog } from "./SheetsColumnMapDialog";

type SheetsHealthBannerProps = {
  health: SheetsHealth;
  datasourceId: string;
  /** For the Drive links. Parsed from the datasource's stored sheet link. */
  spreadsheetId?: string | null;
  /** Called after the owner answers the column notice. */
  onResolved?: (health: SheetsHealth) => void;
  className?: string;
};

/**
 * MOSA-409. The one banner above a paused panel.
 *
 * The API writes the copy and decides the audience, so this component renders
 * what it was handed and adds nothing. In particular it never synthesises a
 * button: a member's notice arrives with `actions: []` and stays a sentence,
 * because a member cannot reconnect Google (QA finding D3 on MOSA-554).
 *
 * The panel itself is not blanked. The grid keeps showing the cached rows it
 * already has, which is what makes this a pause rather than a failure, and
 * the rules, the members and the history are untouched throughout.
 */
export const SheetsHealthBanner: React.FC<SheetsHealthBannerProps> = ({
  health,
  datasourceId,
  spreadsheetId,
  onResolved,
  className = "",
}) => {
  const token = useAuth((state) => state.token);
  const navigate = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mapOpen, setMapOpen] = useState(false);

  const notice = health.notice;
  const actions = actionsFor(notice);

  const reconnect = useCallback(async () => {
    setBusy("reconnect_google");
    setError(null);
    try {
      const result = await startSheetsSignIn(token, currentReturnPath());
      if (result.status === "ready") {
        window.location.assign(result.url);
        return;
      }
      setError(result.message);
    } catch {
      setError("Could not start Google sign-in. Try again.");
    } finally {
      setBusy(null);
    }
  }, [token]);

  const answerColumns = useCallback(
    async (choice: "map" | "keep_both") => {
      if (!health.tab) return;
      setBusy(choice);
      setError(null);
      try {
        const next = await mapSheetsColumns(
          token,
          datasourceId,
          health.tab,
          choice,
        );
        setMapOpen(false);
        onResolved?.(next);
      } catch {
        setError("Could not save that choice. Try again.");
      } finally {
        setBusy(null);
      }
    },
    [datasourceId, health.tab, onResolved, token],
  );

  const run = useCallback(
    (action: SheetsHealthAction) => {
      const href = actionHref(action.id, spreadsheetId);
      if (href) {
        window.open(href, "_blank", "noopener,noreferrer");
        return;
      }
      if (action.id === "reconnect_google") {
        void reconnect();
        return;
      }
      if (action.id === "map_columns") {
        setMapOpen(true);
        return;
      }
      if (action.id === "keep_both_columns") {
        void answerColumns("keep_both");
        return;
      }
      // `pick_again` and `connect_another_sheet` both mean "run the connect
      // flow again". The wizard keeps this datasource, so the panel comes back
      // with its rules, members and history.
      navigate("/connect/sheets");
    },
    [answerColumns, navigate, reconnect, spreadsheetId],
  );

  if (!notice) return null;

  return (
    <>
      <div
        role="alert"
        data-testid="sheets-health-banner"
        data-health-code={notice.code}
        data-health-audience={notice.audience}
        className={`flex flex-col gap-2 rounded-lg border p-3 md:flex-row md:items-center md:justify-between ${className}`}
        style={{
          borderColor: "color-mix(in srgb, var(--od-warning) 45%, transparent)",
          backgroundColor:
            "color-mix(in srgb, var(--od-warning) 10%, transparent)",
        }}
      >
        <div className="min-w-0">
          <p className="m-0 text-sm font-semibold text-od-text">
            {notice.title}
          </p>
          <p className="m-0 mt-0.5 text-xs text-od-muted">{notice.body}</p>
          {error && (
            <p className="m-0 mt-0.5 text-xs text-od-danger">{error}</p>
          )}
        </div>
        {actions.length > 0 && (
          <div className="flex shrink-0 flex-wrap gap-2">
            {actions.map((action) => (
              <Button
                key={action.id}
                variant={action.primary ? "primary" : "secondary"}
                size="sm"
                loading={busy === action.id}
                onClick={() => run(action)}
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}
      </div>
      {mapOpen && (
        <SheetsColumnMapDialog
          health={health}
          busy={busy}
          onChoose={answerColumns}
          onClose={() => setMapOpen(false)}
        />
      )}
    </>
  );
};

export default SheetsHealthBanner;
